import React, { useState, useEffect } from 'react';
import {
  collection, query, where, getDocs,
  doc, getDoc, updateDoc, deleteDoc, setDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

function JobHistory() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [showProfile, setShowProfile] = useState(false);
  const [viewedProfile, setViewedProfile] = useState(null);
  const [showModify, setShowModify] = useState(false);
  const [modifyingRequest, setModifyingRequest] = useState(null);
  const [jobTime, setJobTime] = useState('');
  const [jobDuration, setJobDuration] = useState('8');
  const [jobDescription, setJobDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [submittedReviews, setSubmittedReviews] = useState({});
  const [jobConfirmations, setJobConfirmations] = useState({});
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Fix for mobile — wait for auth to initialize
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchRequests = async () => {
      try {
        const q = query(
          collection(db, 'jobRequests'),
          where('contractorId', '==', user.uid)
        );
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
        setRequests(data);

        const reviewed = {};
        const confsMap = {};
        for (const job of data) {
          const reviewId = `${job.id}_${user.uid}`;
          const reviewDoc = await getDoc(doc(db, 'reviews', reviewId));
          if (reviewDoc.exists()) reviewed[job.id] = true;
          if (job.status === 'completed' || isJobComplete(job)) {
            const confsSnap = await getDocs(
              query(collection(db, 'jobConfirmations'), where('jobRequestId', '==', job.id))
            );
            confsMap[job.id] = confsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          }
        }
        setSubmittedReviews(reviewed);
        setJobConfirmations(confsMap);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchRequests();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const isJobComplete = (request) => {
    if (request.status === 'completed') return true;
    if (!request.jobEndDate) return false;
    if (request.status !== 'accepted' && request.status !== 'completed') return false;
    return new Date() > new Date(request.jobEndDate + 'T23:59:59');
  };

  const getJobCostSummary = (request) => {
    const confs = jobConfirmations[request.id] || [];
    const verified = confs.filter(c => c.status === 'verified');
    const totalHours = verified.reduce((sum, c) => sum + (c.confirmedHours || 0), 0);
    const totalCost = Math.round(totalHours * (request.contractorRate || 0));
    const workerPay = Math.round(totalHours * (request.workerRate || 0));
    const platformFee = totalCost - workerPay;
    return { totalHours, totalCost, workerPay, platformFee, days: verified };
  };

  const handleOpenReview = (request) => {
    setReviewingRequest(request);
    setReviewRating(0);
    setReviewComment('');
    setShowReview(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewRating || !reviewingRequest) return;
    setSubmittingReview(true);
    try {
      const reviewId = `${reviewingRequest.id}_${user.uid}`;
      await setDoc(doc(db, 'reviews', reviewId), {
        jobRequestId: reviewingRequest.id,
        reviewerId: user.uid,
        reviewerType: 'contractor',
        revieweeId: reviewingRequest.workerId,
        revieweeType: 'worker',
        revieweeName: reviewingRequest.workerName,
        rating: reviewRating,
        comment: reviewComment.trim(),
        jobDescription: reviewingRequest.jobDescription,
        jobDate: reviewingRequest.jobDate,
        jobEndDate: reviewingRequest.jobEndDate,
        createdAt: new Date(),
      });
      const workerRef = doc(db, 'workerProfiles', reviewingRequest.workerId);
      const workerDoc = await getDoc(workerRef);
      const workerData = workerDoc.data() || {};
      const newCount = (workerData.reviewCount || 0) + 1;
      const newRating = (((workerData.rating || 0) * (workerData.reviewCount || 0)) + reviewRating) / newCount;
      await updateDoc(workerRef, {
        rating: Math.round(newRating * 10) / 10,
        reviewCount: newCount,
      });
      setSubmittedReviews(prev => ({ ...prev, [reviewingRequest.id]: true }));
      setShowReview(false);
    } catch (err) { console.error(err); }
    setSubmittingReview(false);
  };

  const handleNoShow = async (request) => {
    if (!window.confirm(`Mark ${request.workerName} as a no-show? This will be recorded on their profile permanently.`)) return;
    try {
      await updateDoc(doc(db, 'jobRequests', request.id), {
        status: 'no-show', noShowMarkedAt: new Date(), noShowMarkedBy: 'contractor',
      });
      const workerDoc = await getDoc(doc(db, 'workerProfiles', request.workerId));
      const current = workerDoc.data() || {};
      await updateDoc(doc(db, 'workerProfiles', request.workerId), {
        noShows: (current.noShows || 0) + 1,
      });
      setRequests(prev => prev.map(r => r.id === request.id ? { ...r, status: 'no-show' } : r));
    } catch (err) { console.error(err); }
  };

  const handleCancel = async (request) => {
    const isPending = request.status === 'pending';
    const penalty = isPending ? 0 : Math.ceil(request.totalEstimate * 0.25);
    const refund = isPending ? request.totalEstimate : Math.ceil(request.totalEstimate * 0.75);
    const message = isPending
      ? `Cancel this job request? No penalty applies — this is a free cancellation.`
      : `⚠️ Cancellation Policy\n\nTotal: $${request.totalEstimate?.toLocaleString()}\nPenalty (25%): $${penalty.toLocaleString()}\nRefund (75%): $${refund.toLocaleString()}\n\nDo you want to proceed?`;
    if (!window.confirm(message)) return;
    try {
      if (isPending) {
        await deleteDoc(doc(db, 'jobRequests', request.id));
        setRequests(prev => prev.filter(r => r.id !== request.id));
      } else {
        await updateDoc(doc(db, 'jobRequests', request.id), {
          status: 'cancelled', cancelledAt: new Date(),
          cancelledBy: 'contractor',
          cancellationPenalty: penalty,
          cancellationRefund: refund,
        });
        setRequests(prev => prev.map(r =>
          r.id === request.id
            ? { ...r, status: 'cancelled', cancellationPenalty: penalty, cancellationRefund: refund }
            : r
        ));
      }
    } catch (err) { console.error(err); }
  };

  const handleOpenModify = (request) => {
    setModifyingRequest(request);
    setJobTime(request.jobTime || '');
    setJobDuration(request.jobDuration?.toString() || '8');
    setJobDescription(request.jobDescription || '');
    setShowModify(true);
  };

  const handleSaveModify = async () => {
    if (!modifyingRequest) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'jobRequests', modifyingRequest.id), {
        jobTime, jobDuration: parseInt(jobDuration), jobDescription,
        totalEstimate: Math.ceil(modifyingRequest.contractorRate * parseInt(jobDuration) * modifyingRequest.jobDays),
        updatedAt: new Date(),
      });
      setRequests(prev => prev.map(r =>
        r.id === modifyingRequest.id
          ? { ...r, jobTime, jobDuration: parseInt(jobDuration), jobDescription }
          : r
      ));
      setShowModify(false);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleViewWorkerProfile = async (request) => {
    try {
      const docSnap = await getDoc(doc(db, 'workerProfiles', request.workerId));
      if (docSnap.exists()) {
        setViewedProfile({ type: 'worker', ...docSnap.data() });
        setShowProfile(true);
      }
    } catch (err) { console.error(err); }
  };

  const getTabCount = (tab) => {
    if (tab === 'all') return requests.length;
    if (tab === 'completed') return requests.filter(r => isJobComplete(r)).length;
    if (tab === 'accepted') return requests.filter(r => r.status === 'accepted' && !isJobComplete(r)).length;
    return requests.filter(r => r.status === tab).length;
  };

  const filtered = requests.filter(r => {
    if (filter === 'pending') return r.status === 'pending';
    if (filter === 'accepted') return r.status === 'accepted' && !isJobComplete(r);
    if (filter === 'completed') return isJobComplete(r);
    if (filter === 'declined') return r.status === 'declined';
    if (filter === 'cancelled') return r.status === 'cancelled';
    if (filter === 'no-show') return r.status === 'no-show';
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    return `${h % 12 || 12}:${minutes} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const ratingLabels = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Great', 5: 'Excellent!' };

  const completedCount = requests.filter(r => isJobComplete(r)).length;
  const totalSpent = requests
    .filter(r => isJobComplete(r))
    .reduce((sum, r) => {
      const cost = getJobCostSummary(r);
      return sum + (cost.totalCost || r.totalEstimate || 0);
    }, 0);

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title}>My Job Requests</h2>
        <p style={styles.subtitle}>Track all your sent job requests</p>

        <div style={styles.summaryRow}>
          <div style={styles.summaryCard}>
            <span style={{ ...styles.summaryNum, color: '#856404' }}>{requests.filter(r => r.status === 'pending').length}</span>
            <span style={styles.summaryLabel}>Pending</span>
          </div>
          <div style={styles.summaryCard}>
            <span style={{ ...styles.summaryNum, color: '#155724' }}>{requests.filter(r => r.status === 'accepted' && !isJobComplete(r)).length}</span>
            <span style={styles.summaryLabel}>Active</span>
          </div>
          <div style={styles.summaryCard}>
            <span style={{ ...styles.summaryNum, color: '#2c5282' }}>{completedCount}</span>
            <span style={styles.summaryLabel}>Completed</span>
          </div>
          <div style={styles.summaryCard}>
            <span style={{ ...styles.summaryNum, color: '#E94560' }}>${totalSpent.toLocaleString()}</span>
            <span style={styles.summaryLabel}>Total Spent</span>
          </div>
        </div>

        <div style={styles.tabs}>
          {['pending', 'accepted', 'completed', 'declined', 'cancelled', 'no-show', 'all'].map(tab => (
            <button key={tab}
              style={filter === tab ? styles.tabActive : styles.tab}
              onClick={() => setFilter(tab)}>
              {tab === 'no-show' ? 'No-Show' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span style={styles.tabCount}>{getTabCount(tab)}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div style={styles.emptyState}><p>Loading requests...</p></div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyIcon}>📋</p>
            <p style={styles.emptyTitle}>No {filter} requests</p>
            <p style={styles.emptyText}>
              {filter === 'pending' ? 'You have no pending job requests. Browse workers to send one.' : `No ${filter} requests to show.`}
            </p>
            {filter === 'pending' && (
              <button style={styles.browseBtn} onClick={() => navigate('/browse')}>Browse Workers</button>
            )}
          </div>
        ) : (
          <div style={styles.list}>
            {filtered.map(request => {
              const complete = isJobComplete(request);
              const alreadyReviewed = submittedReviews[request.id];
              const cost = complete ? getJobCostSummary(request) : null;

              return (
                <div key={request.id} style={complete ? styles.cardCompleted : styles.card}>
                  <div style={styles.cardHeader}>
                    <div>
                      <h3 style={styles.workerName}>👷 {request.workerName}</h3>
                      <p style={styles.workerEmail}>{request.workerEmail}</p>
                    </div>
                    <span style={
                      complete ? styles.completedBadge
                      : request.status === 'pending' ? styles.pendingBadge
                      : request.status === 'accepted' ? styles.activeBadge
                      : request.status === 'declined' ? styles.declinedBadge
                      : request.status === 'no-show' ? styles.noShowBadge
                      : styles.cancelledBadge
                    }>
                      {complete ? '🏁 Completed'
                        : request.status === 'pending' ? '⏳ Pending'
                        : request.status === 'accepted' ? '✅ Active'
                        : request.status === 'declined' ? '❌ Declined'
                        : request.status === 'no-show' ? '🚫 No-Show'
                        : '🚫 Cancelled'}
                    </span>
                  </div>

                  <div style={styles.detailsGrid}>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>📅 Dates</span>
                      <span style={styles.detailValue}>
                        {formatDate(request.jobDate)}
                        {request.jobEndDate && request.jobEndDate !== request.jobDate ? ` → ${formatDate(request.jobEndDate)}` : ''}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>🕐 Start Time</span>
                      <span style={styles.detailValue}>{formatTime(request.jobTime)}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>⏱ Hours/Day</span>
                      <span style={styles.detailValue}>{request.jobDuration} hrs</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>📆 Working Days</span>
                      <span style={styles.detailValue}>
                        {request.jobDays} day{request.jobDays !== 1 ? 's' : ''}
                        {(request.includeSaturdays || request.includeSundays) && (
                          <span style={styles.weekendNote}>{' '}(incl. {[request.includeSaturdays && 'Sat', request.includeSundays && 'Sun'].filter(Boolean).join(' & ')})</span>
                        )}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>📍 Job Site</span>
                      <span style={styles.detailValue}>{request.jobAddress}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>💰 Rate</span>
                      <span style={styles.detailValue}>${request.contractorRate}/hr</span>
                    </div>
                  </div>

                  {request.jobDescription && (
                    <div style={styles.descriptionBox}>
                      <span style={styles.detailLabel}>📝 Description</span>
                      <p style={styles.description}>{request.jobDescription}</p>
                    </div>
                  )}

                  {!complete && (
                    <div style={styles.totalBox}>
                      <span style={styles.totalLabel}>Estimated Total</span>
                      <span style={styles.totalAmount}>${request.totalEstimate?.toLocaleString()}</span>
                      <span style={styles.totalNote}>${request.contractorRate}/hr × {request.jobDuration} hrs × {request.jobDays} days</span>
                    </div>
                  )}

                  {complete && cost && (
                    <div style={styles.completedCostBox}>
                      <div style={styles.completedCostHeader}>
                        <span style={styles.completedCostTitle}>💵 Final Invoice Summary</span>
                        <span style={styles.billingBadge}>📅 Billed on next Thursday cycle</span>
                      </div>
                      {cost.days.length > 0 ? (
                        <div style={styles.dailyBreakdown}>
                          {cost.days.sort((a, b) => a.date?.localeCompare(b.date)).map(day => (
                            <div key={day.date} style={styles.dailyRow}>
                              <span style={styles.dailyDate}>{new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                              <span style={styles.dailyHours}>{day.confirmedHours}h</span>
                              <span style={styles.dailyCost}>${Math.round(day.confirmedHours * request.contractorRate)}</span>
                            </div>
                          ))}
                          <div style={styles.dailyTotalRow}>
                            <span style={styles.dailyTotalLabel}>Subtotal</span>
                            <span style={styles.dailyTotalHours}>{cost.totalHours}h</span>
                            <span style={styles.dailyTotalAmount}>${cost.totalCost}</span>
                          </div>
                          <div style={styles.feeBreakdown}>
                            <div style={styles.feeRow}>
                              <span style={styles.feeLabel}>👷 Worker pay ({request.workerRate}/hr)</span>
                              <span style={styles.feeValue}>${cost.workerPay}</span>
                            </div>
                            <div style={styles.feeRow}>
                              <span style={styles.feeLabel}>🏛️ Platform fee (15%)</span>
                              <span style={styles.feeValue}>${cost.platformFee}</span>
                            </div>
                            <div style={{ ...styles.feeRow, borderTop: '1px solid #ddd', paddingTop: '8px', marginTop: '4px' }}>
                              <span style={{ ...styles.feeLabel, fontWeight: 'bold', color: '#E94560' }}>Total Charged</span>
                              <span style={{ ...styles.feeValue, fontWeight: 'bold', color: '#E94560', fontSize: '16px' }}>${cost.totalCost}</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={styles.noConfirmationsNote}>
                          <p style={styles.noConfirmationsText}>⏳ Confirmations still processing — check back shortly.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {request.status === 'cancelled' && (
                    <div style={styles.cancelledBox}>
                      <p style={styles.cancelledTitle}>🚫 Cancelled by Contractor</p>
                      <div style={styles.cancelledRow}>
                        <span style={styles.cancelledLabel}>Penalty (25%)</span>
                        <span style={styles.cancelledPenalty}>-${request.cancellationPenalty?.toLocaleString()}</span>
                      </div>
                      <div style={styles.cancelledRow}>
                        <span style={styles.cancelledLabel}>Refund (75%)</span>
                        <span style={styles.cancelledRefund}>${request.cancellationRefund?.toLocaleString()}</span>
                      </div>
                    </div>
                  )}

                  {request.status === 'no-show' && (
                    <div style={styles.noShowBox}>
                      <p style={styles.noShowTitle}>🚫 Worker did not show up</p>
                      <p style={styles.noShowText}>Recorded on {request.workerName}'s profile permanently.</p>
                    </div>
                  )}

                  {complete && (
                    <div style={alreadyReviewed ? styles.reviewedBanner : styles.reviewBanner}>
                      {alreadyReviewed ? (
                        <span style={styles.reviewedText}>⭐ You've reviewed {request.workerName} for this job — thank you!</span>
                      ) : (
                        <>
                          <div>
                            <p style={styles.reviewBannerTitle}>🎉 Job complete!</p>
                            <p style={styles.reviewBannerText}>How did {request.workerName} perform?</p>
                          </div>
                          <button style={styles.reviewBtn} onClick={() => handleOpenReview(request)}>⭐ Leave Review</button>
                        </>
                      )}
                    </div>
                  )}

                  <div style={styles.actionRow}>
                    <button style={styles.profileBtn} onClick={() => handleViewWorkerProfile(request)}>👤 Worker Profile</button>
                    <button style={styles.messageBtn} onClick={() => navigate(`/chat/${request.id}`)}>💬 Message</button>
                    {request.status === 'pending' && (
                      <button style={styles.modifyBtn} onClick={() => handleOpenModify(request)}>✏️ Modify</button>
                    )}
                    {(request.status === 'pending' || request.status === 'accepted') && !complete && (
                      <button style={styles.cancelBtn} onClick={() => handleCancel(request)}>
                        {request.status === 'pending' ? '🗑 Cancel' : '🚫 Cancel (25% penalty)'}
                      </button>
                    )}
                    {request.status === 'accepted' && !complete && (
                      <button style={styles.noShowBtn} onClick={() => handleNoShow(request)}>🚫 No-Show</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReview && reviewingRequest && (
        <div style={styles.modalOverlay} onClick={() => setShowReview(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>⭐ Review {reviewingRequest.workerName}</h3>
              <button style={styles.modalClose} onClick={() => setShowReview(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.reviewSubtitle}>
                {reviewingRequest.jobDescription} • {formatDate(reviewingRequest.jobDate)}
                {reviewingRequest.jobEndDate !== reviewingRequest.jobDate ? ` → ${formatDate(reviewingRequest.jobEndDate)}` : ''}
              </p>
              <p style={styles.reviewPrompt}>How would you rate {reviewingRequest.workerName}'s work?</p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', margin: '12px 0' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <span key={star}
                    style={{ fontSize: '36px', cursor: 'pointer', color: star <= (reviewHover || reviewRating) ? '#FFB800' : '#ddd', transition: 'color 0.1s' }}
                    onClick={() => setReviewRating(star)}
                    onMouseEnter={() => setReviewHover(star)}
                    onMouseLeave={() => setReviewHover(0)}>★</span>
                ))}
              </div>
              {(reviewHover || reviewRating) > 0 && <p style={styles.ratingLabel}>{ratingLabels[reviewHover || reviewRating]}</p>}
              <label style={styles.fieldLabel}>Comments (optional)</label>
              <textarea style={styles.textarea} placeholder="Describe quality of work, professionalism, punctuality..."
                value={reviewComment} onChange={e => setReviewComment(e.target.value)} rows={4} />
              <div style={styles.modalButtons}>
                <button style={styles.cancelModalBtn} onClick={() => setShowReview(false)}>Cancel</button>
                <button
                  style={reviewRating > 0 && !submittingReview ? styles.submitReviewBtn : styles.submitReviewBtnDisabled}
                  onClick={handleSubmitReview} disabled={!reviewRating || submittingReview}>
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modify Modal */}
      {showModify && modifyingRequest && (
        <div style={styles.modalOverlay} onClick={() => setShowModify(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>✏️ Modify Request</h3>
              <button style={styles.modalClose} onClick={() => setShowModify(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.modifyInfo}>
                <p style={styles.modifyInfoText}>
                  📅 Dates locked — <strong>{formatDate(modifyingRequest.jobDate)}{modifyingRequest.jobEndDate !== modifyingRequest.jobDate ? ` → ${formatDate(modifyingRequest.jobEndDate)}` : ''}</strong>
                </p>
                <p style={styles.modifyInfoText}>👷 Worker: <strong>{modifyingRequest.workerName}</strong></p>
              </div>
              <label style={styles.fieldLabel}>Start Time</label>
              <input style={styles.input} type="time" value={jobTime} onChange={e => setJobTime(e.target.value)} />
              <label style={styles.fieldLabel}>Hours Per Day</label>
              <select style={styles.input} value={jobDuration} onChange={e => setJobDuration(e.target.value)}>
                <option value="4">4 hours</option>
                <option value="6">6 hours</option>
                <option value="8">8 hours (full day)</option>
                <option value="10">10 hours</option>
                <option value="12">12 hours</option>
              </select>
              <label style={styles.fieldLabel}>Job Description</label>
              <textarea style={styles.textarea} placeholder="Describe the work needed..."
                value={jobDescription} onChange={e => setJobDescription(e.target.value)} rows={3} />
              <div style={styles.modifyEstimate}>
                <span style={styles.estimateLabel}>Updated Estimate</span>
                <span style={styles.estimateAmount}>${Math.ceil(modifyingRequest.contractorRate * parseInt(jobDuration) * modifyingRequest.jobDays)}</span>
              </div>
              <div style={styles.modalButtons}>
                <button style={styles.cancelModalBtn} onClick={() => setShowModify(false)}>Cancel</button>
                <button style={saving ? styles.saveModalBtnDisabled : styles.saveModalBtn} onClick={handleSaveModify} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Worker Profile Modal */}
      {showProfile && viewedProfile && (
        <div style={styles.modalOverlay} onClick={() => setShowProfile(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>👷 Worker Profile</h3>
              <button style={styles.modalClose} onClick={() => setShowProfile(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.profileHeader}>
                {viewedProfile.photoData ? (
                  <img src={viewedProfile.photoData} alt="Profile" style={styles.profileAvatar} />
                ) : (
                  <div style={styles.profileAvatarPlaceholder}><span style={{ fontSize: '32px' }}>👤</span></div>
                )}
                <div style={{ flex: 1 }}>
                  <p style={styles.profileName}>{viewedProfile.name}</p>
                  <p style={styles.profileEmail}>{viewedProfile.email}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    {[1,2,3,4,5].map(s => (
                      <span key={s} style={{ color: s <= Math.round(viewedProfile.rating || 0) ? '#FFB800' : '#ddd', fontSize: '16px' }}>★</span>
                    ))}
                    <span style={{ fontSize: '12px', color: '#999', marginLeft: '4px' }}>
                      {viewedProfile.rating > 0 ? `${viewedProfile.rating.toFixed(1)} (${viewedProfile.reviewCount} reviews)` : 'No reviews yet'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                    {viewedProfile.noShows > 0 && <span style={styles.noShowStatBadge}>🚫 {viewedProfile.noShows} No-Show{viewedProfile.noShows > 1 ? 's' : ''}</span>}
                    {viewedProfile.lateCancellations > 0 && <span style={styles.lateCancelStatBadge}>⚠️ {viewedProfile.lateCancellations} Late Cancel{viewedProfile.lateCancellations > 1 ? 's' : ''}</span>}
                    {!viewedProfile.noShows && !viewedProfile.lateCancellations && <span style={styles.reliableBadge}>✅ Reliable</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#E94560' }}>${viewedProfile.hourlyRate}</span>
                  <span style={{ fontSize: '12px', color: '#999', display: 'block' }}>/hr (your rate)</span>
                </div>
              </div>
              {viewedProfile.bio && (
                <div style={styles.profileSection}>
                  <p style={styles.profileSectionLabel}>About</p>
                  <p style={styles.profileSectionValue}>{viewedProfile.bio}</p>
                </div>
              )}
              {viewedProfile.trades?.length > 0 && (
                <div style={styles.profileSection}>
                  <p style={styles.profileSectionLabel}>Trades</p>
                  <div style={styles.profileTags}>{viewedProfile.trades.map(t => <span key={t} style={styles.profileTag}>{t}</span>)}</div>
                </div>
              )}
              <div style={styles.profileSection}>
                <p style={styles.profileSectionLabel}>Compliance</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {viewedProfile.workAuthorized && <span style={styles.compBadge}>✓ Work Authorized</span>}
                  {viewedProfile.backgroundCheckConsent && <span style={styles.compBadge}>✓ BG Check Consent</span>}
                  {viewedProfile.w9Consent && <span style={styles.compBadge}>✓ 1099 Acknowledged</span>}
                  {viewedProfile.insuranceVerified && <span style={styles.compBadge}>🛡️ Carries Own Insurance</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  navbar: { backgroundColor: 'white', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: { padding: '8px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', touchAction: 'manipulation' },
  content: { maxWidth: '860px', margin: '0 auto', padding: '24px 16px' },
  title: { fontSize: '26px', color: '#1a1a2e', marginBottom: '8px' },
  subtitle: { color: '#666', marginBottom: '24px' },
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '24px' },
  summaryCard: { backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '4px' },
  summaryNum: { fontSize: '26px', fontWeight: 'bold', color: '#1a1a2e' },
  summaryLabel: { fontSize: '12px', color: '#999' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' },
  tab: { padding: '8px 14px', backgroundColor: 'white', color: '#666', border: '1px solid #ddd', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', touchAction: 'manipulation' },
  tabActive: { padding: '8px 14px', backgroundColor: '#E94560', color: 'white', border: '1px solid #E94560', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', touchAction: 'manipulation' },
  tabCount: { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '10px', padding: '1px 6px', fontSize: '11px' },
  emptyState: { textAlign: 'center', padding: '60px', color: '#666' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyTitle: { fontSize: '20px', color: '#1a1a2e', marginBottom: '8px' },
  emptyText: { color: '#666', fontSize: '14px', marginBottom: '20px' },
  browseBtn: { padding: '12px 24px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  list: { display: 'flex', flexDirection: 'column', gap: '20px' },
  card: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px' },
  cardCompleted: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px', border: '2px solid #28a745' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' },
  workerName: { fontSize: '17px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  workerEmail: { fontSize: '13px', color: '#999' },
  completedBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  pendingBadge: { backgroundColor: '#fff3cd', color: '#856404', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  activeBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  declinedBadge: { backgroundColor: '#f8d7da', color: '#721c24', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  noShowBadge: { backgroundColor: '#f8d7da', color: '#721c24', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  cancelledBadge: { backgroundColor: '#f0f0f0', color: '#666', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  detailsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  detailLabel: { fontSize: '11px', color: '#999', textTransform: 'uppercase', fontWeight: 'bold' },
  detailValue: { fontSize: '14px', color: '#333' },
  weekendNote: { fontSize: '12px', color: '#E94560' },
  descriptionBox: { marginBottom: '16px' },
  description: { fontSize: '14px', color: '#555', lineHeight: '1.6', marginTop: '6px' },
  totalBox: { backgroundColor: '#fff0f3', borderRadius: '8px', padding: '14px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '4px' },
  totalLabel: { fontSize: '12px', color: '#E94560', fontWeight: 'bold' },
  totalAmount: { fontSize: '26px', fontWeight: 'bold', color: '#E94560' },
  totalNote: { fontSize: '12px', color: '#999' },
  completedCostBox: { backgroundColor: '#fff0f3', border: '2px solid #E94560', borderRadius: '10px', padding: '16px', marginBottom: '16px' },
  completedCostHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' },
  completedCostTitle: { fontSize: '15px', fontWeight: 'bold', color: '#E94560' },
  billingBadge: { backgroundColor: '#fff0f3', color: '#E94560', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', border: '1px solid #E94560' },
  dailyBreakdown: { display: 'flex', flexDirection: 'column', gap: '6px' },
  dailyRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', borderRadius: '6px', padding: '8px 12px' },
  dailyDate: { fontSize: '13px', color: '#333', flex: 1 },
  dailyHours: { fontSize: '13px', color: '#666', width: '40px', textAlign: 'center' },
  dailyCost: { fontSize: '14px', fontWeight: 'bold', color: '#E94560', width: '70px', textAlign: 'right' },
  dailyTotalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff0f3', borderRadius: '6px', padding: '10px 12px', marginTop: '4px' },
  dailyTotalLabel: { fontSize: '13px', fontWeight: 'bold', color: '#E94560', flex: 1 },
  dailyTotalHours: { fontSize: '13px', fontWeight: 'bold', color: '#E94560', width: '40px', textAlign: 'center' },
  dailyTotalAmount: { fontSize: '16px', fontWeight: 'bold', color: '#E94560', width: '70px', textAlign: 'right' },
  feeBreakdown: { backgroundColor: 'white', borderRadius: '8px', padding: '12px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' },
  feeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  feeLabel: { fontSize: '13px', color: '#666' },
  feeValue: { fontSize: '13px', color: '#333' },
  noConfirmationsNote: { backgroundColor: 'white', borderRadius: '8px', padding: '12px' },
  noConfirmationsText: { fontSize: '13px', color: '#856404', textAlign: 'center' },
  cancelledBox: { backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px', padding: '14px', marginBottom: '16px' },
  cancelledTitle: { fontSize: '13px', fontWeight: 'bold', color: '#666', marginBottom: '10px' },
  cancelledRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  cancelledLabel: { fontSize: '13px', color: '#666' },
  cancelledPenalty: { fontSize: '14px', fontWeight: 'bold', color: '#dc3545' },
  cancelledRefund: { fontSize: '14px', fontWeight: 'bold', color: '#28a745' },
  noShowBox: { backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '14px', marginBottom: '16px' },
  noShowTitle: { fontSize: '13px', fontWeight: 'bold', color: '#721c24', marginBottom: '4px' },
  noShowText: { fontSize: '13px', color: '#721c24' },
  reviewBanner: { backgroundColor: '#fff8e1', border: '1px solid #FFB800', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  reviewedBanner: { backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' },
  reviewedText: { fontSize: '13px', color: '#276749', fontWeight: 'bold' },
  reviewBannerTitle: { fontSize: '14px', fontWeight: 'bold', color: '#856404', marginBottom: '2px' },
  reviewBannerText: { fontSize: '13px', color: '#856404' },
  reviewBtn: { padding: '8px 16px', backgroundColor: '#FFB800', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', flexShrink: 0, touchAction: 'manipulation' },
  actionRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  profileBtn: { padding: '9px 14px', backgroundColor: 'transparent', color: '#1a1a2e', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  messageBtn: { padding: '9px 14px', backgroundColor: '#1a1a2e', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  modifyBtn: { padding: '9px 14px', backgroundColor: '#f0f2f5', color: '#333', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  cancelBtn: { padding: '9px 14px', backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  noShowBtn: { padding: '9px 14px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalCard: { backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 12px', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 },
  modalTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e' },
  modalClose: { width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#f0f0f0', border: 'none', cursor: 'pointer', fontSize: '14px', touchAction: 'manipulation' },
  modalBody: { padding: '20px 24px' },
  reviewSubtitle: { fontSize: '13px', color: '#666', marginBottom: '16px', textAlign: 'center' },
  reviewPrompt: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', textAlign: 'center', marginBottom: '4px' },
  ratingLabel: { textAlign: 'center', fontSize: '16px', fontWeight: 'bold', color: '#FFB800', marginBottom: '16px' },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '6px' },
  textarea: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '16px' },
  input: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '15px', boxSizing: 'border-box', marginBottom: '16px' },
  modalButtons: { display: 'flex', gap: '12px' },
  cancelModalBtn: { flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  saveModalBtn: { flex: 2, padding: '12px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
  saveModalBtnDisabled: { flex: 2, padding: '12px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'not-allowed' },
  submitReviewBtn: { flex: 2, padding: '12px', backgroundColor: '#FFB800', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
  submitReviewBtnDisabled: { flex: 2, padding: '12px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'not-allowed' },
  modifyInfo: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '16px' },
  modifyInfoText: { fontSize: '13px', color: '#555', marginBottom: '4px' },
  modifyEstimate: { backgroundColor: '#fff0f3', borderRadius: '8px', padding: '14px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '4px' },
  estimateLabel: { fontSize: '12px', color: '#E94560', fontWeight: 'bold' },
  estimateAmount: { fontSize: '26px', fontWeight: 'bold', color: '#E94560' },
  profileHeader: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  profileAvatar: { width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #E94560', flexShrink: 0 },
  profileAvatarPlaceholder: { width: '72px', height: '72px', borderRadius: '50%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  profileName: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  profileEmail: { fontSize: '13px', color: '#666', marginBottom: '4px' },
  profileSection: { marginBottom: '16px' },
  profileSectionLabel: { fontSize: '11px', color: '#999', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '6px' },
  profileSectionValue: { fontSize: '14px', color: '#333', lineHeight: '1.5' },
  profileTags: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  profileTag: { padding: '4px 10px', backgroundColor: '#f0f0f0', borderRadius: '12px', fontSize: '12px', color: '#444' },
  verifiedBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' },
  compBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  noShowStatBadge: { backgroundColor: '#f8d7da', color: '#721c24', padding: '3px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' },
  lateCancelStatBadge: { backgroundColor: '#fff3cd', color: '#856404', padding: '3px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' },
  reliableBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' },
};

export default JobHistory;